import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/DashboardLayout";
import { KpiCard } from "@/components/Cards";
import { useCountry } from "@/lib/CountryContext";
import { formatPrice } from "@/lib/currency";
import { Users, ShieldCheck, Store, Sparkles, DollarSign, Package } from "lucide-react";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";

export default function AdminDashboard() {
    const [data, setData] = useState(null);
    const { country } = useCountry();

    useEffect(() => {
        api.get("/admin/dashboard").then((r) => setData(r.data));
    }, []);

    if (!data) return <div className="p-10 text-navy-500">Loading…</div>;
    const k = data.kpis;

    return (
        <>
            <PageHeader
                eyebrow="Command centre"
                title="Perx health check"
                description="One view of members, vendors, deals and revenue."
                testid="admin-dashboard-header"
            />

            <div className="px-6 lg:px-12 py-8 space-y-8">
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                    <KpiCard
                        label="Total users"
                        value={k.total_users}
                        delta={`${k.members} members · ${k.brokers} vendors`}
                        icon={Users}
                    />
                    <KpiCard
                        label="Live deals"
                        value={k.live_deals}
                        delta={`${k.product_groups} groups`}
                        icon={Package}
                    />
                    <KpiCard
                        label="Orders"
                        value={k.total_orders}
                        delta="All time"
                        icon={Sparkles}
                    />
                    <KpiCard
                        label="GMV"
                        value={`$${k.gmv.toLocaleString()}`}
                        delta="All-time revenue"
                        icon={DollarSign}
                        accent="pink"
                    />
                </div>

                <div className="grid lg:grid-cols-2 gap-8">
                    <div className="rounded-2xl bg-white border border-navy-100/70 shadow-soft p-6">
                        <div className="text-xs uppercase tracking-[0.3em] text-gold-600 mb-4">
                            User composition
                        </div>
                        <div className="space-y-4">
                            {[
                                ["Members", k.members, Users, "text-navy-700"],
                                ["Vendors", k.brokers, Store, "text-gold-600"],
                                ["Admins", k.admins, ShieldCheck, "text-pinkstar"],
                            ].map(([l, v, Icon, cls]) => (
                                <div
                                    key={l}
                                    className="flex items-center justify-between px-4 py-3 rounded-lg bg-cream border border-navy-100/40"
                                >
                                    <div className="flex items-center gap-3">
                                        <Icon size={18} className={cls} />
                                        <span className="text-navy-700 font-medium">{l}</span>
                                    </div>
                                    <span className="font-display text-2xl text-navy-700">
                                        {v}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="rounded-2xl bg-white border border-navy-100/70 shadow-soft p-6">
                        <div className="text-xs uppercase tracking-[0.3em] text-gold-600 mb-4">
                            Recent orders
                        </div>
                        {data.recent_orders.length === 0 ? (
                            <div className="text-navy-500 text-sm">
                                No orders yet — buy something from the member portal to
                                populate this.
                            </div>
                        ) : (
                            <Table>
                                <TableHeader>
                                    <TableRow className="hover:bg-transparent">
                                        <TableHead className="text-navy-500 text-xs uppercase tracking-widest">
                                            Order
                                        </TableHead>
                                        <TableHead className="text-navy-500 text-xs uppercase tracking-widest">
                                            Amount
                                        </TableHead>
                                        <TableHead className="text-navy-500 text-xs uppercase tracking-widest">
                                            Card
                                        </TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {data.recent_orders.map((o) => (
                                        <TableRow
                                            key={o.id}
                                            data-testid={`recent-order-${o.id}`}
                                        >
                                            <TableCell className="text-navy-700 font-medium text-sm">
                                                {o.deal_id}
                                            </TableCell>
                                            <TableCell className="text-navy-700">
                                                {formatPrice(o.amount, country, { decimals: 2 })}
                                            </TableCell>
                                            <TableCell className="text-navy-500">
                                                •••• {o.card_last4}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}
